// src/pages/Leads/index.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  LinearProgress,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  PersonAdd as LeadIcon,
  FilterList as PipelineIcon,
  Assessment as ConversionIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Call as CallIcon,
  Email as EmailIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import apiClient from '../../api/client';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currency';
import DevOnly from '../../components/DevOnly';

const LeadsManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Reset page to 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, sourceFilter]);

  // Fetch leads overview data
  const { data: leadsOverview, isLoading: overviewLoading, error: overviewError } = useQuery({
    queryKey: ['leads-overview'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/leads/overview');
        return response.data;
      } catch (error) {
        console.error('Error fetching leads overview:', error);
        return {
          metrics: {
            totalLeads: 0,
            qualifiedLeads: 0,
            conversionRate: 0,
            avgDealValue: 0
          },
          sources: [],
          pipeline: []
        };
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch leads data with server-side pagination
  const { data: leadsData = { leads: [], total: 0 }, isLoading: leadsLoading, error: leadsError } = useQuery({
    queryKey: ['leads', searchTerm, statusFilter, sourceFilter, page, rowsPerPage],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (statusFilter !== 'all') params.append('status', statusFilter);
        if (sourceFilter !== 'all') params.append('source', sourceFilter);
        // Use server-side pagination - only fetch the current page
        params.append('limit', String(rowsPerPage));
        params.append('skip', String(page * rowsPerPage));

        const response = await apiClient.get(`/leads?${params.toString()}`);
        console.log('Leads API response:', response.data); // Debug log

        return {
          leads: response.data.leads || [],
          total: response.data.total || 0
        };
      } catch (error) {
        console.error('Error fetching leads:', error);
        return { leads: [], total: 0 };
      }
    },
    staleTime: 2 * 60 * 1000,
    keepPreviousData: true, // Keep previous page data while loading new page
  });

  // Fetch pipeline data
  const { data: pipelineData = [], isLoading: pipelineLoading } = useQuery({
    queryKey: ['leads-pipeline-stages'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/leads/pipeline/stages');
        return response.data || [];
      } catch (error) {
        console.error('Error fetching pipeline:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const formatCurrency = (amount) => formatCurrencyUtil(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  if (overviewError || leadsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load leads data. Please try again later.
        </Alert>
      </Box>
    );
  }

  const metrics = leadsOverview?.metrics || {
    total_leads: 0,
    qualified_leads: 0,
    conversion_rate: 0,
    avg_deal_value: 0
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Leads & Pipeline
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage leads, track conversions, and analyze your sales pipeline
          </Typography>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => alert('Lead creation is internal-only while in beta.')}
            size="large"
          >
            Add Lead
          </Button>
        </DevOnly>
      </Box>

      {/* Metric Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={metrics.conversion_rate > 0 || metrics.avg_deal_value > 0 ? 3 : 6}>
          <MetricCard
            title="Total Leads"
            value={metrics.total_leads}
            icon={<LeadIcon />}
            color="primary"
            isLoading={overviewLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={metrics.conversion_rate > 0 || metrics.avg_deal_value > 0 ? 3 : 6}>
          <MetricCard
            title="Qualified Leads"
            value={metrics.qualified_leads}
            icon={<PipelineIcon />}
            color="success"
            isLoading={overviewLoading}
          />
        </Grid>
        {metrics.conversion_rate > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Conversion Rate"
              value={`${metrics.conversion_rate}%`}
              icon={<ConversionIcon />}
              color="info"
              isLoading={overviewLoading}
            />
          </Grid>
        )}
        {metrics.avg_deal_value > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Avg Deal Value"
              value={formatCurrency(metrics.avg_deal_value)}
              icon={<LeadIcon />}
              color="warning"
              isLoading={overviewLoading}
            />
          </Grid>
        )}
      </Grid>

      {/* Pipeline Overview */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Sales Pipeline
        </Typography>
        {pipelineLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : pipelineData.length === 0 ? (
          <Alert severity="info">
            No pipeline stages configured. Leads are tracked but not assigned to specific pipeline stages yet.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {pipelineData.map((stage) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={stage.id}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent>
                    <Typography 
                      variant="subtitle1" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 500,
                        minHeight: '48px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {stage.name || 'Unknown Stage'}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h4" color="primary.main">
                        {stage.lead_count || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        leads
                      </Typography>
                      {/* Status breakdown */}
                      {(stage.open_leads > 0 || stage.won_leads > 0 || stage.lost_leads > 0) && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {stage.open_leads > 0 && (
                            <Chip 
                              label={`${stage.open_leads} Open`} 
                              size="small" 
                              color="info"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.75rem' }}
                            />
                          )}
                          {stage.won_leads > 0 && (
                            <Chip 
                              label={`${stage.won_leads} Won`} 
                              size="small" 
                              color="success"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.75rem' }}
                            />
                          )}
                          {stage.lost_leads > 0 && (
                            <Chip 
                              label={`${stage.lost_leads} Lost`} 
                              size="small" 
                              color="error"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Value
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(stage.total_value || 0)}
                      </Typography>
                    </Box>
                    {(stage.conversion_probability || 0) > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Conversion Rate
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {stage.conversion_probability}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={stage.conversion_probability}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: 'action.hover',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              backgroundColor: stage.conversion_probability > 50 ? 'success.main' :
                                             stage.conversion_probability > 20 ? 'warning.main' : 'error.main'
                            }
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="qualified">Qualified</MenuItem>
                <MenuItem value="proposal">Proposal</MenuItem>
                <MenuItem value="closed_won">Closed Won</MenuItem>
                <MenuItem value="closed_lost">Closed Lost</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                label="Source"
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <MenuItem value="all">All Sources</MenuItem>
                <MenuItem value="website">Website</MenuItem>
                <MenuItem value="referral">Referral</MenuItem>
                <MenuItem value="social_media">Social Media</MenuItem>
                <MenuItem value="advertising">Advertising</MenuItem>
                <MenuItem value="walk_in">Walk-in</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              onClick={() => navigate('/leads/pipeline')}
              fullWidth
            >
              Pipeline View
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Leads Table */}
      <Paper>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            All Leads {leadsData.leads.length > 0 && `(${leadsData.total || leadsData.leads.length} total)`}
          </Typography>
          {leadsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : leadsData.leads.length === 0 ? (
            <Alert severity="info">
              No leads found. Create your first lead to get started.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Last Contact</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leadsData.leads.map((lead) => (
                    <TableRow key={lead.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {`${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unnamed Lead'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {lead.email || 'No email'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {lead.phone || lead.mobile || 'No phone'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={lead.source || 'Unknown'}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={lead.status || 'New'}
                          color={getLeadStatusColor(lead.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {formatCurrency(lead.estimated_value)}
                      </TableCell>
                      <TableCell>
                        {formatDate(lead.last_contact_date)}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => { setSelectedLead(lead); setDetailsOpen(true); }}
                            title="View Details"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (lead.phone || lead.mobile) {
                                window.open(`tel:${lead.phone || lead.mobile}`);
                              } else {
                                alert('No phone number available for this lead.');
                              }
                            }}
                            title="Call"
                            disabled={!lead.phone && !lead.mobile}
                          >
                            <CallIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (lead.email) {
                                window.open(`mailto:${lead.email}`);
                              } else {
                                alert('No email address available for this lead.');
                              }
                            }}
                            title="Email"
                            disabled={!lead.email}
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                          <DevOnly>
                            <IconButton
                              size="small"
                              onClick={() => alert('Lead editing not implemented yet. This app is read-only, syncing data from Pabau. Changes should be made in Pabau directly.')}
                              title="Edit (Not Available)"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </DevOnly>
                        </Box>
                      </TableCell>
                    </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={leadsData.total || leadsData.leads.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Box>
      </Paper>
      <LeadDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        lead={selectedLead}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
      />
    </Box>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon, color = 'primary', isLoading = false }) => {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography variant="h4" component="div" color={`${color}.main`}>
                {value}
              </Typography>
            )}
          </Box>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Helper function to get lead status color
const getLeadStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'new':
      return 'info';
    case 'contacted':
      return 'primary';
    case 'qualified':
      return 'warning';
    case 'proposal':
      return 'secondary';
    case 'closed_won':
      return 'success';
    case 'closed_lost':
      return 'error';
    default:
      return 'default';
  }
};

export default LeadsManagement;

// Lead Details Modal Component
function LeadDetailsDialog({ open, onClose, lead, formatCurrency, formatDate }) {
  if (!lead) return null;

  const fullName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unnamed Lead';
  const phoneNumber = lead.phone || lead.mobile || null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Lead Details</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h6">{fullName}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip label={lead.source || 'Unknown Source'} variant="outlined" size="small" />
            <Chip label={lead.status || 'New'} color={getLeadStatusColor(lead.status)} size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary">Email: {lead.email || 'N/A'}</Typography>
          <Typography variant="body2" color="text.secondary">Phone: {phoneNumber || 'N/A'}</Typography>
          <Typography variant="body2" color="text.secondary">Estimated Value: {formatCurrency(lead.estimated_value)}</Typography>
          <Typography variant="body2" color="text.secondary">Last Contact: {formatDate(lead.last_contact_date)}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        {phoneNumber && (
          <Button onClick={() => window.open(`tel:${phoneNumber}`)} startIcon={<CallIcon />}>Call</Button>
        )}
        {lead.email && (
          <Button onClick={() => window.open(`mailto:${lead.email}`)} startIcon={<EmailIcon />}>Email</Button>
        )}
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
}